import { useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import { Box, Grid, Paper, Button, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import plusSquareOutline from '@iconify/icons-eva/plus-square-outline';
import editFill from '@iconify/icons-eva/edit-fill';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// hooks
import useTheme from '../../hooks/useTheme';
import { withNotification } from 'wrappers';
// components
import ThemeEditor from './ThemeEditor';

// ----------------------------------------------------------------------

function ThemeSelectorComponent({ appSuccess, appError }) {
  const { themes, activeTheme, setActiveTheme, createTheme, deleteTheme, loading } = useTheme();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [selectingThemeId, setSelectingThemeId] = useState(null);

  const handleSelectTheme = async (themeId) => {
    try {
      setSelectingThemeId(themeId);
      await setActiveTheme(themeId);
      const selectedTheme = themes.find(t => t.id === themeId);
      appSuccess(`Tema "${selectedTheme?.name || 'seleccionado'}" aplicado exitosamente`);
    } catch (error) {
      console.error('Error selecting theme:', error);
      appError(error.message || 'Error al seleccionar el tema');
    } finally {
      setSelectingThemeId(null);
    }
  };

  const handleCreateNew = () => {
    setEditingTheme(null);
    setEditorOpen(true);
  };

  const handleEdit = (theme) => {
    setEditingTheme(theme);
    setEditorOpen(true);
  };

  const handleDelete = async (themeId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este tema?')) {
      try {
        await deleteTheme(themeId);
      } catch (error) {
        console.error('Error deleting theme:', error);
      }
    }
  };

  const handleDuplicate = async (theme) => {
    try {
      // Generate a unique name for the duplicated theme
      const baseName = theme.name;
      let newName = `${baseName} (Copia)`;
      let counter = 1;

      // Check if name already exists and increment counter
      const existingNames = themes.map(t => t.name);
      while (existingNames.includes(newName)) {
        newName = `${baseName} (Copia ${counter})`;
        counter++;
      }

      // Create a copy of the theme with a new name
      const duplicatedThemeData = {
        name: newName,
        config: JSON.parse(JSON.stringify(theme.config || {})), // Deep copy to avoid reference issues
      };

      const newTheme = await createTheme(duplicatedThemeData);
      appSuccess(`Tema "${newTheme.name}" duplicado exitosamente`);

      // Open the editor with the duplicated theme for immediate editing
      setEditingTheme(newTheme);
      setEditorOpen(true);
    } catch (error) {
      console.error('Error duplicating theme:', error);
      appError(error.message || 'Error al duplicar el tema');
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingTheme(null);
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Icon icon={plusSquareOutline} />}
          onClick={handleCreateNew}
          sx={{ mb: 2 }}
        >
          Crear Nuevo Tema
        </Button>
      </Box>

      <Grid container spacing={1.5} dir="ltr">
        {themes.map((theme) => {
          const isSelected = activeTheme && activeTheme.id === theme.id;
          const themeConfig = theme.config || {};

          // Helper to ensure color is a string
          const ensureStringColor = (color, fallback) => {
            if (typeof color === 'string') return color;
            if (Array.isArray(color)) return color[0];
            return fallback;
          };

          const primaryColor = ensureStringColor(themeConfig.colors?.primary?.main, '#2196F3');
          const bgColor = ensureStringColor(themeConfig.colors?.background?.default, '#FFFFFF');
          const textColor = ensureStringColor(themeConfig.colors?.text?.primary, '#212B36');
          const isGlobal = theme.isGlobal;

          return (
            <Grid item xs={6} key={theme.id}>
              <Paper
                variant={isSelected ? 'elevation' : 'outlined'}
                sx={{
                  position: 'relative',
                  bgcolor: bgColor,
                  minHeight: 100,
                  cursor: 'pointer',
                  ...(isSelected && {
                    border: `solid 2px ${primaryColor}`,
                    boxShadow: `0 8px 16px 0 ${alpha(primaryColor, 0.24)}`
                  })
                }}
              >
                {/* Loading overlay */}
                {(loading && selectingThemeId === theme.id) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: 1,
                      zIndex: 10,
                      backdropFilter: 'blur(2px)',
                    }}
                  >
                    <CircularProgress
                      size={48}
                      thickness={4}
                      sx={{
                        color: 'primary.main',
                        mb: 1
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      Aplicando tema...
                    </Typography>
                  </Box>
                )}

                {/* Preview */}
                <Box
                  sx={{
                    p: 1.5,
                    minHeight: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    opacity: (loading && selectingThemeId === theme.id) ? 0.5 : 1,
                    pointerEvents: (loading && selectingThemeId === theme.id) ? 'none' : 'auto',
                  }}
                  onClick={() => !loading && !selectingThemeId && handleSelectTheme(theme.id)}
                >

                  {/* Color Preview */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      mb: 1
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        height: 24,
                        borderRadius: 0.5,
                        bgcolor: primaryColor
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        height: 24,
                        borderRadius: 0.5,
                        bgcolor: bgColor,
                        border: `1px solid ${textColor}`
                      }}
                    />
                  </Box>

                  {/* Theme Name */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: textColor,
                      fontWeight: isSelected ? 'bold' : 'normal',
                      mb: 0.5
                    }}
                  >
                    {theme.name}
                  </Typography>

                  {/* Badge for global themes */}
                  {isGlobal && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: textColor,
                        opacity: 0.7,
                        fontSize: '0.65rem'
                      }}
                    >
                      Predefinido
                    </Typography>
                  )}
                </Box>

                {/* Actions - Bottom */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    left: 4,
                    right: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 0.5,
                    pt: 1,
                    borderTop: `1px solid ${alpha(textColor, 0.1)}`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {!isGlobal && (
                    <>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(theme)}
                          sx={{
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <Icon icon={editFill} width={16} height={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(theme.id)}
                          sx={{
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.lighter', color: 'error.main' }
                          }}
                        >
                          <Icon icon={trash2Outline} width={16} height={16} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Duplicar">
                    <IconButton
                      size="small"
                      onClick={() => handleDuplicate(theme)}
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'info.lighter', color: 'info.main' }
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Selected Indicator */}
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: primaryColor,
                      border: `2px solid ${bgColor}`
                    }}
                  />
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Theme Editor Dialog */}
      <ThemeEditor
        open={editorOpen}
        onClose={handleEditorClose}
        theme={editingTheme}
      />
    </>
  );
}

ThemeSelectorComponent.propTypes = {
  appSuccess: PropTypes.func,
  appError: PropTypes.func,
};

const ThemeSelector = withNotification(ThemeSelectorComponent);

export default ThemeSelector;

